import React, { useEffect } from "react";
import { Divider } from "@UI/Components/Divider/Divider";
import { HStack, VStack } from "@UI/Components/Stack/Stack";
import { Icon } from "@UI/Components/Icon/Icon";
import { Text } from "@UI/Components/Text/Text";
import { Xmark } from "@UI/Components/Icons/Xmark";
import { useServiceRequestCandidates } from "../../hooks/useCandidates";
import { CandidateFilters, ICandidate, ICandidateFilter } from "../../types";
import { CandidateFilterModal } from "./CandidateFilterModal";
import { useDisclosure } from "@UI/hooks/useDisclosure";
import { useParams } from "@Libs/Router/hooks/useParams";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { serviceRequestPathFragment } from "../../config/routes";
import { ServiceCategorySlider } from "../ServiceCategorySlider";
import { EmptyCandidatesPlaceholder } from "./EmptyCandidatesPlaceholder";
import { CandidatesHeader } from "./CandidatesHeader";
import { CandidateListSection } from "./CandidateListSection";
import { DeclineOfferModal } from "./DeclineOffer/DeclineOfferModal";

import { AlertBox } from "@UI/Components/AlertBox/AlertBox";
import { useToast, ToastProps } from "@UI/hooks/useToast";
import { v4 as uuidv4 } from "uuid";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { CandidateOfferModal } from "../ServiceOfferModal";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "../messages";
import { useServiceRequestFlow } from "@modules/ServiceRequest/hooks/useServiceRequestFlow";
import { getConversationStatus } from "../../services/candidates";
import { SpCreatedTaskNoticeBanner } from "./SpCreatedTaskNoticeBanner";
import { PrivateTaskNoResponseBanner } from "./PrivateTaskNoResponseBanner";
import { UnreadMessagesBanner } from "./UnreadMessagesBanner";

const CONVERSATION_OFFCANVAS_ID = /^offcanvas-conversation-(\d+)$/;

const MAX_SP_OFFER_SPOT = 6;

export const CandidatesSection = () => {
  const { serviceRequest } = useServiceRequestFlow();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isDeclineOfferModalOpen,
    onOpen: onDeclineOfferModalOpen,
    onClose: onDeclineOfferModalClose,
  } = useDisclosure();

  const {
    isOpen: isCandidateOfferModalOpen,
    onOpen: onCandidateOfferModalOpen,
    onClose: onCandidateOfferModalClose,
  } = useDisclosure();

  const toast = useToast();
  const { isMobile } = useMediaQuery();

  const [filter, setFilter] = React.useState<ICandidateFilter>(
    CandidateFilters.recommended
  );

  const [selectedCandidate, setSelectedCandidate] =
    React.useState<ICandidate | null>(null);

  const conversationParam = useSearchParams("conversation");

  const { id: serviceRequestId } = useParams([
    serviceRequestPathFragment.SERVICE_REQUEST,
  ]);

  const {
    candidates = [],
    refetch,
    isLoading,
    isSuccess,
    removeCandidateFromList,
    removeUnreadIndicatorFromConversation,
  } = useServiceRequestCandidates({
    serviceRequestId,
    sortBy: filter,
  });

  const isCreatedBySP = serviceRequest?.authorId && serviceRequest?.authorId !== serviceRequest?.userId;

  useEffect(() => {
    (serviceRequest.appliedServiceProvidersCount > 0 || conversationParam) &&
      refetch();
    // serviceRequest can arrive asynchronously (client-side navigation), after this effect's mount-time run
  }, [filter, serviceRequest.appliedServiceProvidersCount, conversationParam]);

  const hasShownDeclinedToastRef = React.useRef(false);

  useEffect(() => {
    if (!isSuccess || hasShownDeclinedToastRef.current || !conversationParam) return;

    const isInCandidates = candidates.some(
      (c) => c.conversationId === Number(conversationParam)
    );
    if (isInCandidates) return;

    getConversationStatus(Number(conversationParam)).then((conv) => {
      if (!conv?.declinedReason) return;
      hasShownDeclinedToastRef.current = true;
      showToast({
        status: "info",
        description: (
          <FormattedMessage
            {...messages["serviceRequest.declineOffer.alreadyDeclined"]}
            values={{ firstname_sp: conv.spFirstName }}
          />
        ),
      });
    }).catch(() => undefined);
  }, [isSuccess, conversationParam]);

  // Opening a conversation offcanvas (from a card, the SP profile sidepanel, or ?conversation=)
  // marks it read server-side; mirror that in the UI. The sidepanel badge is server-rendered ERB,
  // so it has to be removed from the DOM directly.
  const markConversationAsRead = (conversationId: number) => {
    removeUnreadIndicatorFromConversation(conversationId);
    document
      .querySelectorAll(
        `[data-bs-target="#offcanvas-conversation-${conversationId}"]`
      )
      .forEach((trigger) => {
        trigger.classList.remove("border-dark");
        trigger.querySelector(".unread-count-badge")?.remove();
      });
  };

  useEffect(() => {
    const onOffcanvasShow = (event: Event) => {
      const id = (event.target as HTMLElement | null)?.id;
      const conversationId = id?.match(CONVERSATION_OFFCANVAS_ID)?.[1];
      if (!conversationId) return;

      markConversationAsRead(Number(conversationId));
    };

    document.addEventListener("show.bs.offcanvas", onOffcanvasShow);
    return () =>
      document.removeEventListener("show.bs.offcanvas", onOffcanvasShow);
  }, []);

  // Arriving via ?conversation= (e.g. an email link): the legacy script opens the offcanvas on
  // DOMContentLoaded, before the listener above exists, and before candidates have loaded.
  // Once they have, mirror the server-side read here.
  useEffect(() => {
    if (!isSuccess || !conversationParam) return;
    if (!document.getElementById(`offcanvas-conversation-${conversationParam}`)) return;

    markConversationAsRead(Number(conversationParam));
  }, [isSuccess, conversationParam]);

  const hasUnread = candidates.some((c) => c.unreadConversationCommentsCount > 0);

  // Hand-rolled eased scroll: native `behavior: "smooth"` is instant in some browsers
  // and too abrupt over long distances.
  const scrollToFirstUnread = () => {
    const target = document.querySelector("[data-unread-candidate]");
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const startY = window.scrollY;
    const distance = rect.top + rect.height / 2 - window.innerHeight / 2;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) {
      window.scrollTo(0, startY + distance);
      return;
    }

    const duration = Math.min(1000, Math.max(500, Math.abs(distance) / 2));
    const startTime = performance.now();
    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const step = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      window.scrollTo(0, startY + distance * easeInOutCubic(progress));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const unreadBanner = hasUnread && !isLoading && (
    <UnreadMessagesBanner onSeeMore={scrollToFirstUnread} />
  );

  const handleFilterChange = (value: ICandidateFilter) => {
    setFilter(value);
    onClose();
  };

  const handleCandidateSelection = (
    conversationId: ICandidate["conversationId"]
  ) => {
    setSelectedCandidate(() =>
      candidates?.find((c) => c.conversationId === conversationId)
    );
  };

  const handleDeclineOfferModalOpen = (
    conversationId: ICandidate["conversationId"]
  ) => {
    handleCandidateSelection(conversationId);
    onDeclineOfferModalOpen();
  };
  const handleCandidateOfferModalOpen = (
    conversationId: ICandidate["conversationId"]
  ) => {
    handleCandidateSelection(conversationId);
    onCandidateOfferModalOpen();
  };

  const showToast = (toastData: Pick<ToastProps, "status" | "description">) => {
    const { status, description } = toastData;
    const toastId = uuidv4();
    toast({
      id: toastId,
      status,
      position: isMobile ? "bottom" : "top-right",
      duration: 2000,
      isClosable: true,
      render: () => (
        <AlertBox
          status={status}
          background="white"
          childrenContainerStyle={{ justifyContent: "start" }}
          marginTop={!isMobile ? "80px" : "initial"}
        >
          <HStack justifyContent="space-between" flexGrow={1}>
            <Text m="0" ml={3} color="gray.700">
              {description}
            </Text>
            <Icon
              as={Xmark}
              color="gray.700"
              cursor="pointer"
              onClick={() => toast.close(toastId)}
            />
          </HStack>
        </AlertBox>
      ),
    });
  };

  const offers = React.useMemo(
    () => candidates?.filter((c) => !!c.serviceOffer || c.offerWithdrawn) || [],
    [candidates]
  );
  const discussions = React.useMemo(
    () => candidates?.filter((c) => !c.serviceOffer && !c.offerWithdrawn) || [],
    [candidates]
  );

  if (serviceRequest.isPrivate || isCreatedBySP) {
    return (
      <VStack w="full" pt="7" alignItems="start" flexGrow={1}>
        <VStack w="full" alignItems="start" spacing="5">
          {unreadBanner}
          {serviceRequest.authorId !== serviceRequest.userId && (
            <SpCreatedTaskNoticeBanner
              authorName={serviceRequest.authorFirstName || ""}
              onDeclineClick={() => {
                if (candidates[0]) {
                  handleDeclineOfferModalOpen(candidates[0].conversationId);
                }
              }}
            />
          )}

          <CandidatesHeader
            title={
              isCreatedBySP ? (
                <FormattedMessage
                  {...messages["serviceRequest.candidates.private.offerProposedHeader"]}
                />
              ) : (
                <FormattedMessage
                  {...messages["serviceRequest.candidates.private.header"]}
                />
              )
            }
          />

          <CandidateListSection
            candidates={candidates}
            isLoading={isLoading}
            emptyPlaceholder={<EmptyCandidatesPlaceholder />}
            onCandidateOfferModalOpen={handleCandidateOfferModalOpen}
            removeUnreadIndicatorFromConversation={
              removeUnreadIndicatorFromConversation
            }
            onDeclineOfferModalOpen={handleDeclineOfferModalOpen}
          />

          {serviceRequest.authorId === serviceRequest.userId &&
            !isLoading &&
            !candidates.some((c) => c.serviceOffer) && (
              <PrivateTaskNoResponseBanner
                spName={candidates[0]?.firstName || ""}
                postTaskHref={`/task_creation?p_category=${serviceRequest.pCategoryId}&step=2${serviceRequest.sCategoryId ? `&s_category=${serviceRequest.sCategoryId}` : ""}`}
              />
            )}
        </VStack>

        <CandidateOfferModal
          candidate={selectedCandidate}
          isOpen={isCandidateOfferModalOpen}
          onClose={onCandidateOfferModalClose}
          onDeclineOfferModalOpen={handleDeclineOfferModalOpen}
        />

        <DeclineOfferModal
          isOpen={isDeclineOfferModalOpen}
          onClose={onDeclineOfferModalClose}
          conversationId={selectedCandidate?.conversationId}
          serviceProviderName={selectedCandidate?.firstName || ""}
          isOfferMade={!!selectedCandidate?.serviceOffer}
          isCreatedBySP={isCreatedBySP}
          showToast={showToast}
          removeCandidateFromList={removeCandidateFromList}
          onDidNotAgree={() => { window.location.href = "/home_listposter"; }}
        />
      </VStack>
    );
  }

  return (
    <VStack w="full" pt="7" alignItems="start" flexGrow={1}>
      <CandidateFilterModal
        isOpen={isOpen}
        onClose={onClose}
        filter={filter}
        onFilterChange={handleFilterChange}
      />

      <VStack w="full" alignItems="start" spacing="5">
        {unreadBanner}
        <CandidatesHeader
          title={
            <FormattedMessage
              {...messages["serviceRequest.candidates.offers.header"]}
            />
          }
          countLabel={`${offers.length}/${MAX_SP_OFFER_SPOT}`}
          filter={filter}
          onFilterClick={onOpen}
          showFilter={candidates.length > 0}
        />

        {offers.length >= MAX_SP_OFFER_SPOT && (
          <AlertBox
            status="warning"
            background="orange.100"
            w="full"
            childrenContainerStyle={{
              justifyContent: "start",
              gap: 2,
            }}
          >
            <Text fontSize="sm" as="span">
              <FormattedMessage
                {...messages["serviceRequest.candidates.offers.warning"]}
              />
            </Text>
          </AlertBox>
        )}

        <CandidateListSection
          candidates={offers}
          isLoading={isLoading}
          emptyPlaceholder={
            <EmptyCandidatesPlaceholder
              message={
                <>
                  <FormattedMessage
                    {...messages["serviceRequest.candidates.offers.empty_1"]}
                  />
                  <br />
                  <FormattedMessage
                    {...messages["serviceRequest.candidates.offers.empty_2"]}
                  />
                </>
              }
            />
          }
          onCandidateOfferModalOpen={handleCandidateOfferModalOpen}
          removeUnreadIndicatorFromConversation={
            removeUnreadIndicatorFromConversation
          }
          onDeclineOfferModalOpen={handleDeclineOfferModalOpen}
        />

        <CandidatesHeader
          title={
            <FormattedMessage
              {...messages["serviceRequest.candidates.discussions.header"]}
            />
          }
          countLabel={discussions.length}
        />

        <CandidateListSection
          candidates={discussions}
          isLoading={isLoading}
          emptyPlaceholder={
            <Text mt="20px" textAlign="center" w="full" color="gray.600">
              <FormattedMessage
                {...messages["serviceRequest.candidates.discussions.empty"]}
              />
            </Text>
          }
          onCandidateOfferModalOpen={handleCandidateOfferModalOpen}
          removeUnreadIndicatorFromConversation={
            removeUnreadIndicatorFromConversation
          }
          onDeclineOfferModalOpen={handleDeclineOfferModalOpen}
        />

        {serviceRequest.appliedServiceProvidersCount < 1 && !isLoading && (
          <>
            <Divider
              color="sand.300"
              my={{ base: "20px !important", lg: "32px !important" }}
            />
            <ServiceCategorySlider />
          </>
        )}
      </VStack>

      <CandidateOfferModal
        candidate={selectedCandidate}
        isOpen={isCandidateOfferModalOpen}
        onClose={onCandidateOfferModalClose}
        onDeclineOfferModalOpen={handleDeclineOfferModalOpen}
      />

      <DeclineOfferModal
        isOpen={isDeclineOfferModalOpen}
        onClose={onDeclineOfferModalClose}
        conversationId={selectedCandidate?.conversationId}
        serviceProviderName={selectedCandidate?.firstName || ""}
        isOfferMade={!!selectedCandidate?.serviceOffer}
        isCreatedBySP={isCreatedBySP}
        showToast={showToast}
        removeCandidateFromList={removeCandidateFromList}
        onDidNotAgree={() => { window.location.href = "/home_listposter"; }}
      />
    </VStack>
  );
};
